import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";

/**
 * MoreQuestions landing.
 *
 * Animation: wordmark builds → black question fragments rain in across
 * a white stage, accelerating and growing huge until the visible canvas
 * is near-black → a slow settle wash closes the remaining slivers →
 * landing reveal fades in to the hero.
 *
 * Black ink on white, all the way through the rain. The settle wash
 * (the only black "panel" anywhere) eases in over an already-darkened
 * screen, so it reads as the questions completing the takeover rather
 * than a black panel cutting in.
 *
 * Every visible tuning knob lives in the TUNING block right below.
 * Dial these to taste, watching it run; no other constants are spread
 * through the choreography.
 */

// ─── TUNING ─────────────────────────────────────────────────────────
// Wordmark
const MARK_BUILD_MS    = 1700;     // how long the wordmark fade-up takes
const SKIP_HINT_DELAY  = 1200;     // delay before "tap to skip" appears
// Question rain
const TOTAL            = 140;      // total fragments (was 95)
const START_DELAY      = 2200;     // wait before rain starts, lets wordmark land
const GAP_START_MS     = 380;      // first inter-spawn gap
const GAP_DECAY        = 0.90;     // each gap *= this (slow → frantic)
const GAP_FLOOR_MS     = 10;       // never faster than this
const SIZE_BASE_MIN    = 22;       // px font-size for first (smallest) spawn
const SIZE_GROWTH      = 220;      // late base = SIZE_BASE_MIN + prog² × GROWTH
const SIZE_JITTER_MIN  = 0.75;     // size = base × random(MIN, MAX)
const SIZE_JITTER_MAX  = 1.60;
const WEIGHT_MIN       = 600;      // font-weight ramps from MIN to MAX with prog
const WEIGHT_MAX       = 700;      // (Plex Mono ships 400/500/600/700)
const LATE_PHASE_PROG  = 0.40;     // beyond this prog, switch to grid-gap placement
// Grid coverage for the late phase: targets wherever white still survives
const GRID_W           = 12;
const GRID_H           = 8;
// Settle wash — eases in over an already-near-black screen, so this is
// slow & gentle, not abrupt.
const SETTLE_LEAD_MS   = 600;      // start settle this many ms BEFORE last spawn finishes
const SETTLE_FADE_MS   = 1200;     // settle fade duration (was 500)
// Hold on solid black, then reveal
const HOLD_MS          = 1100;     // pure-black hold after settle completes
const REVEAL_FADE_MS   = 1500;     // hero fade-in
// Fallback so the animation still runs if web-font loading fails entirely
const FONT_LOAD_FALLBACK_MS = 1500;
// ────────────────────────────────────────────────────────────────────

// Module-level latch: the intro animation plays only on a fresh page load.
// Soft-nav back to "/" (e.g. clicking the wordmark in the Topbar) skips
// straight to the revealed hero state. A hard refresh re-loads the module
// and resets this to false, so the animation plays fresh as expected.
// Deliberately module-scoped (not React state) so it survives unmount /
// remount across route changes within a single session. No persistence
// to localStorage by design (app rule).
let hasPlayed = false;

export default function Landing() {
  const { me } = useAuth();
  const navigate = useNavigate();

  const ctaTarget = me?.signed_in ? "/practice" : "/access-code";

  const stageRef    = useRef<HTMLDivElement | null>(null);
  const markRef     = useRef<HTMLDivElement | null>(null);
  const settleRef   = useRef<HTMLDivElement | null>(null);
  const revealRef   = useRef<HTMLDivElement | null>(null);
  const skipRef     = useRef<HTMLButtonElement | null>(null);
  const finishedRef = useRef<boolean>(false);

  useEffect(() => {
    const stageEl   = stageRef.current;
    const markEl    = markRef.current;
    const settleEl  = settleRef.current;
    const revealEl  = revealRef.current;
    const skipEl    = skipRef.current;
    if (!stageEl || !markEl || !settleEl || !revealEl || !skipEl) return;
    const stage:  HTMLDivElement    = stageEl;
    const mark:   HTMLDivElement    = markEl;
    const settle: HTMLDivElement    = settleEl;
    const reveal: HTMLDivElement    = revealEl;
    const skip:   HTMLButtonElement = skipEl;

    // Wire the timing constants into CSS custom properties so the
    // tuning block at the top of this file is the single source of
    // truth — the CSS keyframes consume var(--mark-build-ms) etc.
    const root = mark.closest(".landing-root") as HTMLElement | null;
    if (root) {
      root.style.setProperty("--mark-build-ms",   `${MARK_BUILD_MS}ms`);
      root.style.setProperty("--settle-fade-ms",  `${SETTLE_FADE_MS}ms`);
      root.style.setProperty("--reveal-fade-ms",  `${REVEAL_FADE_MS}ms`);
    }

    // StrictMode safety: a `cancelled` flag captured in this effect's
    // closure. The deferred font-load promise can resolve after the
    // effect has been torn down (React 18 dev intentionally double-
    // mounts); we bail at that point instead of racing a fresh run().
    let cancelled = false;

    const timers: number[] = [];
    const setTimer = (cb: () => void, ms: number) => {
      const id = window.setTimeout(cb, ms);
      timers.push(id);
    };
    const clearAll = () => {
      for (const id of timers) window.clearTimeout(id);
      timers.length = 0;
    };
    const clearNodes = () => {
      stage.querySelectorAll(".landing-q").forEach((n) => n.remove());
    };

    // Tile-based coverage map. The late phase picks the least-covered
    // tile and spawns a fragment there with mild jitter, so the rain
    // genuinely drives the screen toward black instead of relying on
    // pure random luck.
    const coverage = new Float32Array(GRID_W * GRID_H);
    const cellArea = (100 / GRID_W) * (100 / GRID_H);

    function pickGapCell(): { cx: number; cy: number } {
      // weakest cell wins; tie-break randomly among the bottom ~20%.
      let min = Infinity;
      for (let i = 0; i < coverage.length; i++) {
        if (coverage[i] < min) min = coverage[i];
      }
      const ceiling = min + 0.5;
      const candidates: number[] = [];
      for (let i = 0; i < coverage.length; i++) {
        if (coverage[i] <= ceiling) candidates.push(i);
      }
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      return { cx: pick % GRID_W, cy: Math.floor(pick / GRID_W) };
    }

    function markCovered(xVw: number, yVh: number, sizePx: number) {
      // Approximate the fragment's ink footprint in vw/vh and credit
      // each grid cell it touches by overlap area × size weight. Use
      // an assumed 60% text-width vs container ratio and 10× size for
      // pseudo-width (mono font, ~10px per char at 1em is a rough
      // mid-estimate; the absolute scale doesn't matter — only the
      // RELATIVE coverage signal).
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const halfWvw = ((sizePx * 5) / vw) * 100;     // half the assumed text width
      const halfHvh = ((sizePx * 0.55) / vh) * 100;  // half the assumed text height
      const x0 = Math.max(0, xVw - halfWvw);
      const x1 = Math.min(100, xVw + halfWvw);
      const y0 = Math.max(0, yVh - halfHvh);
      const y1 = Math.min(100, yVh + halfHvh);
      const cellWvw = 100 / GRID_W;
      const cellHvh = 100 / GRID_H;
      const cx0 = Math.floor(x0 / cellWvw);
      const cx1 = Math.min(GRID_W - 1, Math.floor((x1 - 0.001) / cellWvw));
      const cy0 = Math.floor(y0 / cellHvh);
      const cy1 = Math.min(GRID_H - 1, Math.floor((y1 - 0.001) / cellHvh));
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cx = cx0; cx <= cx1; cx++) {
          coverage[cy * GRID_W + cx] += sizePx / cellArea;
        }
      }
    }

    function run() {
      if (cancelled) return;
      finishedRef.current = false;
      clearAll();
      clearNodes();
      coverage.fill(0);
      mark.classList.remove("in");
      settle.classList.remove("in");
      settle.style.animation = "";
      settle.style.opacity = "";
      reveal.classList.remove("in");
      skip.classList.remove("show", "dark");
      void mark.offsetWidth; // restart keyframe

      mark.classList.add("in");
      setTimer(() => skip.classList.add("show"), SKIP_HINT_DELAY);

      let t = 0;
      let gap = GAP_START_MS;
      let lastDelay = 0;

      for (let i = 0; i < TOTAL; i++) {
        const delay = START_DELAY + t;
        lastDelay = delay;
        const idx = i;
        setTimer(() => {
          if (cancelled) return;
          const prog = idx / TOTAL;
          const el = document.createElement("div");
          el.className = "landing-q";
          el.textContent = QUESTIONS[idx % QUESTIONS.length];
          // size: small at start, grows hard near the end
          const base = SIZE_BASE_MIN + prog * prog * SIZE_GROWTH;
          const size = base * (SIZE_JITTER_MIN + Math.random() *
                              (SIZE_JITTER_MAX - SIZE_JITTER_MIN));
          el.style.fontSize = Math.round(size) + "px";
          el.style.maxWidth = (28 + Math.random() * 55) + "vw";
          // heavy late, lighter early
          el.style.fontWeight = String(
            Math.round(WEIGHT_MIN + (WEIGHT_MAX - WEIGHT_MIN) * prog)
          );

          // placement: early random; late targets coverage gaps with jitter
          let x: number, y: number;
          if (prog < LATE_PHASE_PROG) {
            x = Math.random() * 100;
            y = Math.random() * 100;
          } else {
            const { cx, cy } = pickGapCell();
            const cellWvw = 100 / GRID_W;
            const cellHvh = 100 / GRID_H;
            // center of the cell + ±60% jitter — biases hard to gap centers
            x = (cx + 0.5 + (Math.random() - 0.5) * 0.6) * cellWvw;
            y = (cy + 0.5 + (Math.random() - 0.5) * 0.6) * cellHvh;
          }
          el.style.left = x + "vw";
          el.style.top  = y + "vh";
          el.style.zIndex = String(10 + idx); // late giants paint over the wordmark

          markCovered(x, y, size);

          stage.appendChild(el);
          void el.offsetWidth;
          el.classList.add("show");
        }, delay);
        t += gap;
        gap *= GAP_DECAY;
        if (gap < GAP_FLOOR_MS) gap = GAP_FLOOR_MS;
      }

      // Settle starts BEFORE the rain finishes so its slow ease-in
      // overlaps the last few rapid-fire spawns — the screen is
      // already mostly black; settle just closes the remaining slivers.
      const settleStart = Math.max(lastDelay - SETTLE_LEAD_MS, lastDelay * 0.6);
      const settleDone  = lastDelay + 200;
      const revealStart = settleDone + HOLD_MS;

      setTimer(() => {
        settle.classList.add("in");
        skip.classList.add("dark");
      }, settleStart);

      // Clear all fragments at the moment the reveal starts (improves
      // on the prototype, which left them in place and brittle to
      // randomness — late .landing-q nodes have z-index > 60 and would
      // otherwise paint over the hero text).
      setTimer(() => {
        clearNodes();
        reveal.classList.add("in");
        skip.classList.remove("show");
        finishedRef.current = true;
        hasPlayed = true;
      }, revealStart);
    }

    function skipToLanding() {
      if (finishedRef.current) return;
      clearAll();
      clearNodes();
      settle.style.animation = "none";
      settle.style.opacity = "1";
      reveal.classList.add("in");
      skip.classList.remove("show");
      finishedRef.current = true;
      hasPlayed = true;
    }

    const onClick = () => skipToLanding();
    const onKey   = () => skipToLanding();
    const onWheel = () => skipToLanding();
    const onTouch = () => skipToLanding();
    // Stage handles click-anywhere; the skip button gets its own click
    // listener since it sits outside .landing-stage in the DOM and
    // wouldn't otherwise propagate to the stage listener.
    stage.addEventListener("click", onClick);
    skip.addEventListener("click", onClick);
    window.addEventListener("keydown",     onKey);
    window.addEventListener("wheel",       onWheel, { passive: true });
    window.addEventListener("touchstart",  onTouch, { passive: true });

    // Soft-nav re-entry (e.g. user clicked the wordmark in the Topbar
    // to come back here): the animation already ran once this session,
    // so skip straight to the revealed hero state. Reuse skipToLanding
    // rather than duplicating its DOM moves -- it's the same end state.
    if (hasPlayed) {
      skipToLanding();
      return () => {
        cancelled = true;
        clearAll();
        clearNodes();
        stage.removeEventListener("click", onClick);
        skip.removeEventListener("click", onClick);
        window.removeEventListener("keydown",    onKey);
        window.removeEventListener("wheel",      onWheel);
        window.removeEventListener("touchstart", onTouch);
      };
    }

    // Explicit font load. The wordmark uses Space Grotesk 700; the
    // rain uses Plex Mono 600-700. Wait for BOTH face files before
    // starting the visible build, so the wordmark animation doesn't
    // re-flow mid-keyframe when the web fonts arrive.
    const fontPromises: Promise<unknown>[] = [];
    if (document.fonts && typeof document.fonts.load === "function") {
      try {
        fontPromises.push(document.fonts.load("700 1em 'Space Grotesk'"));
        fontPromises.push(document.fonts.load("600 1em 'IBM Plex Mono'"));
        fontPromises.push(document.fonts.load("700 1em 'IBM Plex Mono'"));
      } catch {
        /* old browsers — fall through to the fallback timer */
      }
    }
    let fontsResolved = false;
    Promise.all(fontPromises).then(() => {
      if (cancelled || fontsResolved) return;
      fontsResolved = true;
      run();
    });
    setTimer(() => {
      if (cancelled || fontsResolved) return;
      fontsResolved = true;
      run();
    }, FONT_LOAD_FALLBACK_MS);

    return () => {
      cancelled = true;
      clearAll();
      clearNodes();
      stage.removeEventListener("click", onClick);
      skip.removeEventListener("click", onClick);
      window.removeEventListener("keydown",    onKey);
      window.removeEventListener("wheel",      onWheel);
      window.removeEventListener("touchstart", onTouch);
    };
  }, []);

  function goStart() {
    navigate(ctaTarget);
  }

  return (
    <div className="landing-root">
      <div className="landing-stage" ref={stageRef}>
        <div className="landing-settle" ref={settleRef} />
      </div>

      {/* Wordmark is a sibling of the stage — not a child — so nothing
          appended to the stage during the rain can reflow it. position:
          fixed plus a stable transform-origin keep it dead-centred. */}
      <div className="landing-mark" ref={markRef}>
        MoreQuestions
      </div>

      {/* Skip affordance. Hidden by default (opacity 0); the effect
          flips the `show` class on after SKIP_HINT_DELAY and flips it
          off when the reveal fires, so the button is visible during
          the animation and gone once the landing shows. Click is
          wired in the effect alongside the existing tap-anywhere
          handlers. */}
      <button
        type="button"
        className="landing-skip"
        ref={skipRef}
        aria-label="Skip animation"
      >
        ▸ skip
      </button>

      <div className="landing-reveal" ref={revealRef}>
        <header className="landing-nav">
          {/* Wordmark routes back to "/" -- when reached via Topbar from
              elsewhere in the app, the module-level hasPlayed latch
              short-circuits the intro animation. Already on landing
              here: this nav is just for symmetry with the Topbar
              wordmark's behaviour. */}
          <Link
            to="/"
            className="landing-nav__brand"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            MoreQuestions
          </Link>
          <button
            type="button"
            className="landing-nav__cta"
            onClick={goStart}
          >
            Start practicing
          </button>
        </header>
        <main className="landing-hero">
          {/* Deterministic two-line wrap. The previous version relied on
              max-width: 16ch to break the headline; same value as the
              prototype, but the React build's `ch` measurement against
              Space Grotesk drifted vs the standalone HTML page (font-load
              timing differs), pushing "trust." to a third line. Explicit
              <br /> removes the dependency on ch entirely so the headline
              is ALWAYS two lines: "Practice questions" / "you can actually
              trust." with the em underline cleanly spanning the second line. */}
          <h1 className="landing-hero__h1">
            Practice questions<br />
            you can <em className="landing-hero__em">actually trust.</em>
          </h1>
          <p className="landing-hero__lede">
            Every question is checked by running the real calculation - so
            you practice the right answer, not an AI&rsquo;s confident
            guess.
          </p>
          <div className="landing-hero__row">
            <button
              type="button"
              className="landing-hero__go"
              onClick={goStart}
            >
              Start practicing free
            </button>
            <Link to="/about" className="landing-hero__ghost">
              See why this matters
            </Link>
          </div>
        </main>
      </div>
    </div>
  );
}

// Question fragments — cycled with modulo across TOTAL spawn positions.
// Realistic intro-accounting exam questions, mixed financial + managerial,
// with deliberate length variation (~12 short, ~12 medium, ~12 long) so the
// rain has visual texture at the random 28-83vw per-fragment widths.
const QUESTIONS: string[] = [
  "Compute straight-line depreciation on a $48,000 truck.",
  "A machine cost $32,000, salvage $4,000, 7 years - find annual SL depreciation.",
  "Equipment costs $80,000 with $10,000 salvage and 100,000 hours; what's the units-of-production rate per hour?",
  "Compute ending inventory under FIFO for May.",
  "Inventory 200 @ $5, purchases 300 @ $6 - what's COGS using LIFO if 350 sold?",
  "Beginning inventory 100 @ $8, purchases 300 @ $10 - compute weighted-average cost per unit and ending inventory for 150 units.",
  "Adjusting entry for $1,200 prepaid insurance used?",
  "Wages of $4,800 accrued but unpaid - what's the year-end adjusting entry?",
  "Office supplies of $2,400 were recorded as an asset; $900 remains at year-end - what's the adjusting entry?",
  "Cash collected from customers this quarter?",
  "Net income is $42,000 on accrual basis - what's net cash from operations?",
  "Revenue earned but unbilled is $7,500; cash received in advance is $3,200 - reconcile accrual to cash.",
  "Estimate the allowance for doubtful accounts.",
  "Aging shows 5% of $80,000 receivables likely uncollectible - what's bad debt expense?",
  "Allowance has a $400 credit balance; aging estimates $5,200 uncollectible - what's the year-end adjustment?",
  "Compute semiannual interest on a $100,000 bond at 5%.",
  "What's the interest expense on $200,000 bonds at 6% issued at par?",
  "$500,000 bonds at 5% stated, 4% market, issued July 1 - compute the issue price and first interest expense.",
  "Compute the adjusted bank balance for May.",
  "Outstanding checks $1,400; deposit in transit $800 - what's the adjusted balance?",
  "Bank statement $6,250; outstanding checks $920, deposits in transit $1,100, service charge $25 - what's the adjusted balance?",
  "Break-even units at $20 CM and $80,000 fixed cost?",
  "Selling price $50, variable $30, fixed $120,000 - find break-even sales dollars.",
  "At a price of $80, variable cost $48, and fixed cost $192,000 per month, how many units are needed for $60,000 target profit?",
  "What's the contribution margin per unit?",
  "Sales $400,000, variable cost $240,000 - find the CM ratio and operating leverage.",
  "Volume rose from 8,000 to 10,000 units; total cost from $46,000 to $54,000 - find variable rate and fixed cost via high-low.",
  "Find variable cost per unit using high-low.",
  "Overhead applied at $14 per machine hour over 4,200 hours - what's total applied?",
  "Estimated overhead $360,000 on 24,000 DLH - compute the predetermined rate and apply it to Job 412 with 180 hours.",
  "Compute total cost of Job 207 with $4,200 DM and $3,000 DL.",
  "Job 318 used $5,000 materials, $6,500 labor, overhead at 80% of labor - total cost?",
  "Should we accept a special order of 2,000 units at $18 when variable cost is $14 and there is idle capacity?",
  "Identify the relevant cost of the new machine.",
  "Supplier quotes Part X at $22; in-house variable $19, fixed $5 - make or buy?",
  "Producing Z costs $14 variable plus $6 avoidable fixed per unit; supplier offers $19 each - buy 10,000 units?",
];
